import { useEffect } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { useToasts } from "react-toast-notifications";
import { useWeb3Onboard } from "@src/hooks/useWeb3Onboard";

import {
    RadioButtons,
    NoStrat,
    Suspended,
    MainVesting,
    ClaimModal,
} from "@src/components";
import {
    useAppSelector,
    useWeb3Auth,
    useContract,
    useAppDispatch,
} from "@src/hooks";
import {
    setCurrId,
    setProcessing,
    updateStrategies,
    updateStrategy,
} from "@src/features/vesting";

import { UserVesting } from "@src/ts/interfaces";
import { ATS, CHAIN_ID, VESTING_DETAILS } from "@src/config";

import VestingABI from "@src/abi/DecubateVestingV2.json";
import { Banner } from "@src/components/vesting/Banner";
import { setClaimModalOpen } from "@src/features/layout";

const VestingPage: React.FC = () => {
    useWeb3Auth();
    const router = useRouter();
    const idx = Number(router.query.idx);

    const { contract } = VESTING_DETAILS[idx] || {};
    const { addToast } = useToasts();
    const dispatch = useAppDispatch();
    const { account, connectedChain } = useWeb3Onboard();
    const dcb_vesting = useContract(contract, VestingABI, true);

    const { curr_id, loading, attempted } = useAppSelector(
        (state) => state.vesting,
    );

    const strategies =
        useAppSelector((state) => state.vesting.strategies[idx]) || [];

    const has_strategies = strategies.length > 0;

    const current =
        has_strategies && strategies.find(({ id }) => id === curr_id);

    const is_correct_chain =
        CHAIN_ID === parseInt(connectedChain?.id || "0", 16);

    useEffect(() => {
        if (dcb_vesting && is_correct_chain)
            dispatch(updateStrategies(dcb_vesting, account, idx));
    }, [account, dcb_vesting]);

    useEffect(() => {
        if (has_strategies && !curr_id) dispatch(setCurrId(strategies[0].id));
    }, [has_strategies, curr_id]);

    const handleClick = async () => {
        if (ATS) {
            dispatch(setClaimModalOpen(true));
            return;
        }
        return handleRelease();
    };

    const handleRelease = async (callback?: () => void) => {
        dispatch(setProcessing(true));
        addToast("Awaiting successful transaction", {
            appearance: "info",
        });
        try {
            const tx = await dcb_vesting.claimDistribution(curr_id, account);
            await tx.wait();

            addToast("You have successfully claimed your tokens.", {
                appearance: "success",
            });
            dispatch(updateStrategy(dcb_vesting, account, idx, curr_id));
            if (callback) callback();
        } catch (err) {
            addToast(err.data?.message || err.message, { appearance: "error" });
            dispatch(setProcessing(false));
        }
    };

    const renderPage = (current: UserVesting | undefined) => {
        if (loading || !attempted) {
            return null;
        }
        if (!current) {
            return <NoStrat />;
        }
        if (current?.revoked || current?.disabled) {
            return (
                <Suspended
                    current={current}
                    disabled={current?.disabled}
                    handleRelease={handleClick}
                />
            );
        }

        return <MainVesting current={current} handleRelease={handleClick} />;
    };

    return (
        <div className="flex-1 container max-w-screen-lg mx-auto p-5 text-center">
            {has_strategies && <Banner />}

            {VESTING_DETAILS.length > 1 && (
                <div className="text-left mb-4">
                    <Link href="/tokens">
                        <a className="flex items-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6 mr-2"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16l-4-4m0 0l4-4m-4 4h18"
                                />
                            </svg>
                            Back
                        </a>
                    </Link>
                </div>
            )}

            {(!attempted && !loading) ||
                (strategies?.length > 1 && (
                    <RadioButtons
                        options={strategies.map(({ id, name: label }) => ({
                            id,
                            label,
                        }))}
                        curr={curr_id}
                        setCurr={(id: number) => dispatch(setCurrId(id))}
                    />
                ))}
            {renderPage(current)}

            <ClaimModal handleRelease={handleRelease} />
        </div>
    );
};

export default VestingPage;
